import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { api } from "../lib/api";
import { ImageUpload } from "../components/ImageUpload";

const empty = {
  title: "Menu de la semaine", entrees: [], plats: [], desserts: [], cafe: true,
  drinks: ["Soft 33 cl", "Eau 33 cl", "Bière pression 25 cl", "Verre de vin"],
  price: 24.90, photo_url: "", start_date: "", end_date: "", available: true, published: true, order: 0,
};

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean);

export default function AdminWeekly() {
  const [menus, setMenus] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/weekly-menus").then((r) => setMenus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (m) => setEditing({ ...m, entreesText: toLines(m.entrees), platsText: toLines(m.plats), dessertsText: toLines(m.desserts) });

  const save = async (e) => {
    e.preventDefault();
    const { entreesText, platsText, dessertsText, ...rest } = editing;
    const payload = {
      ...rest,
      entrees: fromLines(entreesText || ""),
      plats: fromLines(platsText || ""),
      desserts: fromLines(dessertsText || ""),
      price: Number(editing.price),
      photo_url: editing.photo_url || null,
      start_date: editing.start_date || null,
      end_date: editing.end_date || null,
    };
    if (!payload.entrees.length || !payload.plats.length || !payload.desserts.length) {
      toast.error("Ajoutez au moins un choix par catégorie (entrées, plats, desserts)");
      return;
    }
    try {
      if (editing.id) await api.put(`/admin/weekly-menus/${editing.id}`, payload);
      else await api.post("/admin/weekly-menus", payload);
      toast.success("Menu de la semaine enregistré");
      setEditing(null);
      load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const togglePublish = async (m) => {
    try { await api.put(`/admin/weekly-menus/${m.id}`, { ...m, published: !m.published }); load(); } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce menu ?")) return;
    try { await api.delete(`/admin/weekly-menus/${id}`); toast.success("Supprimé"); load(); } catch { toast.error("Erreur"); }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-weekly">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif-elegant text-3xl font-bold">Menu de la semaine</h1>
        {menus.length === 0 && (
          <button data-testid="weekly-add-btn" onClick={() => openEdit({ ...empty })} className="btn-gold !py-2 text-sm"><Plus size={16} /> Créer le menu</button>
        )}
      </div>
      <p className="text-sm text-gray-400 mb-8">Un seul menu à 24,90 € TTC : renouvelez chaque semaine les choix d'entrées, de plats et de desserts (un choix par ligne).</p>

      {editing && (
        <form onSubmit={save} className="ic-card !transform-none p-6 mb-8 space-y-4" data-testid="weekly-edit-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="weekly-form-title" required className="ic-input" placeholder="Titre *" value={editing.title} onChange={set("title")} />
            <input data-testid="weekly-form-price" required type="number" step="0.01" className="ic-input" placeholder="Prix €" value={editing.price} onChange={set("price")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="gold-label block mb-2">Entrées au choix (1 par ligne) *</label>
              <textarea data-testid="weekly-form-entrees" rows="5" className="ic-input" placeholder={"Bruschetta\nVelouté de potiron"} value={editing.entreesText} onChange={set("entreesText")} />
            </div>
            <div>
              <label className="gold-label block mb-2">Plats au choix (1 par ligne) *</label>
              <textarea data-testid="weekly-form-plats" rows="5" className="ic-input" placeholder={"Spaghetti Carbonara\nPizza Margherita"} value={editing.platsText} onChange={set("platsText")} />
            </div>
            <div>
              <label className="gold-label block mb-2">Desserts au choix (1 par ligne) *</label>
              <textarea data-testid="weekly-form-desserts" rows="5" className="ic-input" placeholder={"Tiramisu\nPanna Cotta"} value={editing.dessertsText} onChange={set("dessertsText")} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label className="gold-label block mb-2">Photo du menu</label>
              <ImageUpload testId="weekly-form-photo" value={editing.photo_url} onChange={(u) => setEditing({ ...editing, photo_url: u })} />
            </div>
            <div>
              <label className="gold-label block mb-1">Disponible du</label>
              <input data-testid="weekly-form-start" type="date" className="ic-input" value={editing.start_date || ""} onChange={set("start_date")} />
            </div>
            <div>
              <label className="gold-label block mb-1">au</label>
              <input data-testid="weekly-form-end" type="date" className="ic-input" value={editing.end_date || ""} onChange={set("end_date")} />
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input data-testid="weekly-form-available" type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} className="accent-[#D4AF37]" />
              Disponible
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input data-testid="weekly-form-published" type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-[#D4AF37]" />
              Publié
            </label>
          </div>
          <div className="flex gap-3">
            <button type="submit" data-testid="weekly-form-save" className="btn-gold !py-2 text-sm">Enregistrer</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold !py-2 text-sm">Annuler</button>
          </div>
        </form>
      )}

      {menus.map((m) => (
        <div key={m.id} className="ic-card !transform-none p-6 mb-5" data-testid={`admin-weekly-card-${m.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-serif-elegant text-xl font-semibold text-[#D4AF37]">{m.title}</h2>
            <div className="flex items-center gap-2">
              <span className={`text-xs rounded-full border px-3 py-1 ${m.published ? "text-[#009246] border-[#009246]/40" : "text-gray-400 border-gray-600"}`}>
                {m.published ? "Publié" : "Brouillon"}
              </span>
              <button data-testid={`weekly-edit-${m.id}`} onClick={() => openEdit(m)} className="p-2 text-gray-400 hover:text-[#D4AF37]"><Pencil size={16} /></button>
              <button data-testid={`weekly-toggle-${m.id}`} onClick={() => togglePublish(m)} className="p-2 text-gray-400 hover:text-[#D4AF37]" title={m.published ? "Dépublier" : "Publier"}>
                {m.published ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button data-testid={`weekly-delete-${m.id}`} onClick={() => remove(m.id)} className="p-2 text-gray-400 hover:text-[#ff6b81]"><Trash2 size={16} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
            <div><p className="gold-label mb-2">Entrées</p>{(m.entrees || []).map((c) => <p key={c} className="text-gray-300">• {c}</p>)}</div>
            <div><p className="gold-label mb-2">Plats</p>{(m.plats || []).map((c) => <p key={c} className="text-gray-300">• {c}</p>)}</div>
            <div><p className="gold-label mb-2">Desserts</p>{(m.desserts || []).map((c) => <p key={c} className="text-gray-300">• {c}</p>)}</div>
          </div>
          <p className="text-xs text-gray-500 mt-4">{m.start_date} → {m.end_date} · {m.available ? "Disponible" : "Indisponible"} · {Number(m.price).toFixed(2).replace(".", ",")} €</p>
        </div>
      ))}
    </div>
  );
}
